import React, { useState } from "react";
import "../../../../style/block.scss";
import "../../../../style/feature.scss";
import "./style.scss";
import "../../../../style/buttons.scss";
import { format } from "date-fns";
import { MdKeyboardArrowUp, MdChevronLeft, MdChevronRight } from "react-icons/md";
import SingleExercise from "../single-exercise";
import { ExerciseScheduleList } from "../../../models/ExerciseScheduleList";
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter";
import LoadableComponent from "../../../../features/Loading/LoadableComponent";
import useLocaleByLanguage from "../../../../utils/hooks/useLocaleByLanguage";
import { useTranslation } from "react-i18next";
import EAIconButton from "../../shared/Buttons/EAIconButton";
import { pl } from "date-fns/locale";

interface IProps {
  dateFromState: Date;
  exercisesList: ExerciseScheduleList;
  loading: boolean;
  onRefreshExercises: () => void;
  nextDay?: () => void;
  previousDay?: () => void;
}

const ScheduleMobile: React.FC<IProps> = ({
  dateFromState,
  exercisesList,
  loading,
  onRefreshExercises,
  nextDay,
  previousDay,
}) => {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const [showPastHours, setShowPastHours] = useState(false);

  const getEventsOnDay = () => {
    // Use Polish locale for day name lookup since exercisesList keys are always in Polish
    const polishDayName = format(dateFromState, "EEEE", { locale: pl });
    const currentDay = capitalizeFirstLetter(polishDayName) as keyof ExerciseScheduleList;
    return exercisesList[currentDay] || [];
  };

  const allEvents = getEventsOnDay();
  const now = new Date();
  const isToday = format(dateFromState, "yyyy-MM-dd") === format(now, "yyyy-MM-dd");

  // Filter by end time: hide only when the event has ended (so long events 8:00–16:00 stay visible during the day)
  const filteredEvents =
    showPastHours || !isToday ? allEvents : allEvents.filter((event) => new Date(event.closeTime) > now);

  // Check if there are ended events to show (e.g. "Pokaż zakończone zajęcia")
  const hasPastHours = isToday && allEvents.some((event) => new Date(event.closeTime) <= now);

  // Group events by full hour
  const eventsByFullHour: { [hour: number]: typeof filteredEvents } = {};
  filteredEvents.forEach((event) => {
    const fullHour = new Date(event.startTime).getHours();
    if (!eventsByFullHour[fullHour]) {
      eventsByFullHour[fullHour] = [];
    }
    eventsByFullHour[fullHour].push(event);
  });

  const hoursWithEvents = Object.keys(eventsByFullHour)
    .map(Number)
    .sort((a, b) => a - b);

  const isNoData = filteredEvents.length === 0;

  // Handle bottom navigation with scroll to top
  const handlePreviousDay = () => {
    if (previousDay) {
      previousDay();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNextDay = () => {
    if (nextDay) {
      nextDay();
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  return (
    <React.Fragment>
      <LoadableComponent loading={loading}>
        <table className="exercise-schedule-desktop">
          <thead>
            <tr>
              <th className="days">
                <span>{format(dateFromState, "cccc", { locale: locale })}</span>
                <b>{format(dateFromState, "dd.MM")}</b>
              </th>
            </tr>
          </thead>
        </table>

        {isNoData ? (
          <div className="schedule-mobile__no-data">{t("brak_wydarzen_tego_dnia")}</div>
        ) : (
          <>
            {hasPastHours && !showPastHours && (
              <div className="schedule-mobile__past-hours">
                <EAIconButton
                  title="Pokaż wcześniejsze godziny"
                  icon={<MdKeyboardArrowUp />}
                  variant="icon"
                  onClick={() => setShowPastHours(true)}
                />
              </div>
            )}

            <div className="schedule-mobile__content">
              {hoursWithEvents.map((hour) => (
                <div key={hour} className="schedule-mobile__hour-row">
                  <p className="text-bold text-centered">
                    {" "}
                    {format(dateFromState, "EEEE", { locale: pl })} {String(hour).padStart(2, "0")}:00
                  </p>

                  <div className="schedule-mobile__exercises-column">
                    {eventsByFullHour[hour].map((event, index) => (
                      <div className="singleone" key={index}>
                        <SingleExercise exercise={event} onRefreshExercises={onRefreshExercises} />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {nextDay && previousDay && (
              <div className="schedule-mobile__bottom-navigation">
                <EAIconButton
                  title="Poprzedni dzień"
                  icon={<MdChevronLeft />}
                  variant="outlined"
                  onClick={handlePreviousDay}
                />
                <EAIconButton
                  title="Następny dzień"
                  icon={<MdChevronRight />}
                  variant="outlined"
                  onClick={handleNextDay}
                />
              </div>
            )}
          </>
        )}
      </LoadableComponent>
    </React.Fragment>
  );
};;

export default ScheduleMobile;
